<template>
  <v-dialog :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields['title'] }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>
      <v-card-text>
        <div>
          <label class="label-input">Section Name</label>
          <v-text-field v-model="fields['title']" label="Name" />
        </div>
        <div>
          <label class="label-input">Title</label>
          <ckeditor :editor="editor" v-model="fields['titleBannerMedia']" :config="editorConfig" />
        </div>
        <div class="mt-6">
          <label class="label-input">Image Background</label>
          <div class="group-item">
            <v-row>
              <v-col cols="12" md="6">
                <p>On Desktop</p>
                <UploadImage
                  @callback="obj => handleUploadImageBackground(obj)"
                  :image="{ imageSrc: fields.imageBackground.desktop }"
                  :height="`200`"
                  :typeUpload="'image'"
                  @callbackDelete="handleRemoveImageBackground"
                />
              </v-col>
              <v-col cols="12" md="6">
                <p>On Mobile</p>
                <UploadImage
                  @callback="obj => handleUploadImageBackgroundMobile(obj)"
                  :image="{ imageSrc: fields.imageBackground.mobile }"
                  :height="`200`"
                  :typeUpload="'image'"
                  @callbackDelete="handleRemoveImageBackgroundMobile"
                />
              </v-col>
            </v-row>
          </div>
        </div>
        <div class="mt-6">
          <label class="label-input">Video URL</label>
          <div class="group-item">
            <v-btn style="color: white" class="mb-2" v-if="fields.videoUrl && checkIsYoutubeUrl()" color="red" small>
              <v-icon class="mr-1">mdi-youtube</v-icon> Youtube
            </v-btn>
            <div class="d-flex align-center gap-6">
              <v-text-field v-model="fields.videoUrl" label="Video URL" hide-details />
              <v-btn
                v-if="fields.videoUrl && !checkIsYoutubeUrl()"
                @click="handlePreviewVideo"
                text
                color="primary"
                small
              >
                <v-icon>mdi-reload</v-icon> Reload
              </v-btn>
            </div>
            <div class="video-wrapper" v-if="fields.videoUrl && !checkIsYoutubeUrl()">
              <div class="wrap-video">
                <video width="100%" height="auto" autoplay muted loop playsinline :ref="'videoPlayer'">
                  <source data-attribute="card-video" type="video/mp4" :src="fields.videoUrl" />
                </video>
              </div>
            </div>
            <div class="video-wrapper" v-if="fields.videoUrl && checkIsYoutubeUrl()">
              <iframe
                width="100%"
                height="315"
                class="items-grid-media-item-video-inner"
                data-attribute="items-grid-media-item-video-inner"
                :src="normalizeYoutubeEmbedUrl(fields.videoUrl)"
                title="YouTube video player"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share;"
                referrerpolicy="strict-origin-when-cross-origin"
                allowfullscreen
              ></iframe>
            </div>
          </div>
        </div>
        <div class="mt-6">
          <label class="label-input">Section Settings</label>
          <div class="group-item">
            <div>
              <div class="font-weight-medium">Spacing</div>
              <v-row>
                <v-col cols="6">
                  <v-checkbox v-model="fields.padding.top" label="Top Spacing" dense hide-details />
                </v-col>
                <v-col cols="6">
                  <v-checkbox v-model="fields.padding.bottom" label="Bottom Spacing" dense hide-details />
                </v-col>
              </v-row>
            </div>
            <div class="mt-6">
              <div class="font-weight-medium mb-3">Container Colors</div>
              <v-row>
                <v-col cols="12" md="12">
                  <ColorPicker
                    v-model="fields.colors.headerTitleColor"
                    name="headerTitleColor"
                    label="Header Title Color"
                    default-color="#ffffff"
                  />
                </v-col>
              </v-row>
            </div>
            <div class="mt-6">
              <div class="font-weight-medium mb-3">Play Icon Colors</div>
              <v-row>
                <v-col cols="12" md="6">
                  <ColorPicker
                    v-model="fields.colors.playIconBackground"
                    name="playIconBackground"
                    label="Play Icon Background Color"
                    default-color="#ffffff"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <ColorPicker
                    v-model="fields.colors.playIconColor"
                    name="playIconColor"
                    label="Play Icon Color"
                    default-color="#000000"
                  />
                </v-col>
              </v-row>
            </div>
            <div class="mt-6">
              <div class="font-weight-medium mb-3">Overlay Colors</div>
              <v-row>
                <v-col cols="12" md="12">
                  <ColorPicker
                    v-model="fields.colors.overlayColor"
                    name="overlayColor"
                    label="Overlay Color"
                    default-color="#000000"
                  />
                </v-col>
                <v-col cols="12" md="12">
                  <v-subheader class="pl-0"> Background Image Opacity </v-subheader>
                  <v-slider
                    v-model="fields.overlayOpacity"
                    :step="0.1"
                    :max="1"
                    :min="0"
                    hide-details
                    thumb-label="always"
                  >
                  </v-slider>
                </v-col>
              </v-row>
            </div>
          </div>
        </div>
      </v-card-text>
      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import { updateHtmlFromData } from '@/views/website/products/components/description/common/html/update';
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import ColorPicker from './common/color-picker.vue';
import { isYoutubeUrl, normalizeYoutubeEmbedUrl } from '@/views/website/products/components/description/common/utils';

export default {
  name: 'CardMediaSlider',
  components: {
    UploadImage,
    ColorPicker,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: {
        title: '',
        titleBannerMedia: '',
        colors: {
          playIconBackground: '',
          playIconColor: '',
          headerTitleColor: '',
          overlayColor: '',
        },
        padding: {
          top: true,
          bottom: true,
        },
        imageBackground: {
          mobile: '',
          desktop: '',
        },
        videoUrl: '',
        overlayOpacity: 0,
      },
    };
  },
  mounted() {
    this.fields = {
      ...this.fields,
      ...this.sectionEditor.sectionExtract,
    };
  },
  methods: {
    handlePreviewVideo(index) {
      const video = this.$refs.videoPlayer[index];
      if (video) {
        video.load();
        video.play();
      }
    },
    handleUploadImageBackground(obj) {
      if (obj && obj.imageSrc) {
        this.fields.imageBackground.desktop = obj.imageSrc;
      }
    },
    handleUploadImageBackgroundMobile(obj) {
      if (obj && obj.imageSrc) {
        this.fields.imageBackground.mobile = obj.imageSrc;
      }
    },
    handleRemoveImageBackground() {
      this.fields.imageBackground.desktop = '';
    },
    handleRemoveImageBackgroundMobile() {
      this.fields.imageBackground.mobile = '';
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const updateHtml = updateHtmlFromData.sectionBannerMedia(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields['title'],
        html: updateHtml,
      });
      this.$emit('close');
    },
    checkIsYoutubeUrl() {
      return isYoutubeUrl(this.fields.videoUrl);
    },
    normalizeYoutubeEmbedUrl(url) {
      return normalizeYoutubeEmbedUrl(url);
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .ck-editor__editable_inline {
  min-height: 100px !important;
}
::v-deep .ck-editor__editable {
  min-height: 100px !important;
}
.video-wrapper {
  margin-top: 16px;
  background: rgba(0, 0, 0, 0.767);
  .wrap-video {
    max-width: 300px;
    margin: 0 auto;
    video {
      display: block;
      width: 100%;
      height: auto;
    }
  }
}
</style>
